import React, { useState, useRef } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../contexts/AuthContext";
import guardiansService from "../services/guardiansService";
import "../assets/guardianDataTable.css";

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const GuardianDataTable = ({
  guardians,
  loading = false,
  currentPage = 1,
  pageSize = 50,
  totalRecords = 0,
  sortField: externalSortField = "unique_guardian_number",
  sortDirection: externalSortDirection = "asc",
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onEdit,
  onView,
  onDelete,
  onDeclineDoc,
  onDismiss,
  onNotInterested,
  onMarkAsPaid,
  onSignAgreement,
  onStartRent,
  onChangeRent,
  onBlacklist,
  onUnblacklist,
  onDecline,
  onAssignOfficer,
  onSetPayment,
  onSetAsRegistered,
  onMoveGuardian,
  onArchiveGuardian,
  onSendPaymentDetails,
  onSendPaymentReminder,
  permissions = [],
  columnVisibility = {
    unique_guardian_number: true,
    name: true,
    status: true,
    email: true,
    notes: true,
    payment_confirmed: true,
    property_name: false,
    license_fee: false,
    actions: true,
    salutation: false,
    job_title: false,
    address_1: false,
    address_2: false,
    address_3: false,
    town: false,
    postcode: false,
    hear_about: false,
    registration_source: false,
    phone_number: false,
    mobile_number: false,
    nationality: false,
    religion: false,
    gender: false,
    date_of_birth: false,
    national_insurance_number: false,
    on_newsletter_list: false,
    on_voluntary_work: false,
    assigned_on: false,
    officer: false,
    email_receive_marketing: false,
    updated_by: false,
    share_data_other_organisation: false,
    placed_date: false,
    end_date: false,
    notice_date_ntq: false,
    created_at: false,
    updated_at: false
  }
}) => {
  const { user } = useAuth();

  // Local state for inline notes editing
  const [editedNotes, setEditedNotes] = useState({});
  const originalNotes = useRef({});

  // Permission checking function
  const hasPermission = (permission) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission);
  };

  // Handle notes text change (local state only)
  const handleNotesChange = (guardianId, value) => {
    setEditedNotes((prev) => ({ ...prev, [guardianId]: value }));
  };

  // Handle notes blur - save to backend
  const handleNotesBlur = async (guardianId) => {
    const newValue = editedNotes[guardianId];
    const original = originalNotes.current[guardianId];

    // Only save if the value actually changed
    if (newValue === undefined || newValue === original) return;

    try {
      const response = await guardiansService.changeField(guardianId, "notes", newValue);
      if (response && response.success) {
        toast.success("Internal notes updated");
        // Update the original ref so subsequent blur without changes won't re-save
        originalNotes.current[guardianId] = newValue;
      } else {
        throw new Error(response?.message || "Failed to update notes");
      }
    } catch (error) {
      console.error("Error updating notes:", error);
      toast.error(error.response?.data?.message || error.message || "Failed to update notes");
      // Revert on error
      setEditedNotes((prev) => ({ ...prev, [guardianId]: original }));
    }
  };

  // Get the current notes value for a guardian (edited or original)
  const getNotesValue = (guardian) => {
    if (editedNotes[guardian.id] !== undefined) {
      return editedNotes[guardian.id];
    }
    // Track the original value on first render
    if (originalNotes.current[guardian.id] === undefined) {
      originalNotes.current[guardian.id] = guardian.notes || "";
    }
    return guardian.notes || "";
  };

  // Server-side sorting handler
  const handleSort = (field) => {
    if (externalSortField === field) {
      onSortChange(field, externalSortDirection === "asc" ? "desc" : "asc");
    } else {
      onSortChange(field, "asc");
    }
  };

  // Calculate pagination info
  const totalPages = Math.ceil(totalRecords / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalRecords);



  const getStatusBadge = (status) => {
    if (!status) return null;
    return (
      <span
        className="status-badge"
        style={{ color: status.color }}
      >
        {status.name}
      </span>
    );
  };


  const renderActionButtons = (guardian) => {
    console.log("👤 Rendering actions for guardian:", guardian);
    console.log("🏷️ Guardian status:", guardian.status);
    console.log("🆔 Status ID:", guardian.status?.id);
    const status_id = guardian.status?.id;
    // ✅ FIX: Use latest_rent (most recent regardless of status) for "Change rent" and "Dismiss" buttons
    // ✅ Use latest_not_ended_or_failed_rent (active rent) for other buttons
    const latestRent = guardian.latest_rent; // Most recent rent regardless of status (for "Change rent" and "Dismiss")
    const latestNotEndedRent = guardian.latest_not_ended_or_failed_rent; // Active rent (for other buttons)
    const hasInitialPayments = latestNotEndedRent?.initialPayments?.length > 0;
    const hasSignedAgreement = latestNotEndedRent?.signed_license_agreement;

    // Laravel assign logic: $assign && canAssign (Registered (1), Documents Required (2), Previous Guardian (7))
    const canAssign = [1, 2, 7].includes(status_id);

    // Laravel canHaveChangedPaymentsOnRent logic: Payment Pending (4), Awaiting Key Collection (5), Placed (6), Vetted (12)
    // Laravel method only checks status, NOT latestRent existence - latestRent is checked separately in button condition
    const canChangeRent = [4, 5, 6, 12].includes(status_id);

    return (
      <div className="btn-group" role="group">
        {/* Show button - Permission check: guardians.show */}
        {hasPermission('guardians.show') && (
          <button
            className="btn btn-sm btn-warning btn-action"
            onClick={() => onView(guardian.id)}
            title="Show Guardian"
          >
            Show
          </button>
        )}

        {/* Edit button - Permission check: guardians.edit */}
        {hasPermission('guardians.edit') && (
          <button
            className="btn btn-sm btn-primary btn-action"
            onClick={() => onEdit(guardian.id)}
            title="Edit Guardian"
          >
            Edit
          </button>
        )}

        {/* Assign button - Laravel logic: $assign && guardians.assign permission + canAssign statuses */}
        {canAssign && hasPermission('guardians.assign') && (
          <button
            className="btn btn-sm btn-info btn-action"
            onClick={() => onAssignOfficer(guardian.id)}
            title="Assign Guardian"
          >
            Assign
          </button>
        )}

        {/* Set Payment button - Property Assigned (3) + multiple permission checks + rent exists */}
        {/* Laravel exact condition: status_id == 3 && permissions && !empty($latestRent) && $latestRent->room */}
        {/* Note: Laravel uses latestRent (not latest_not_ended_or_failed_rent) for this button */}
        {status_id === 3 && 
         hasPermission('property-application.payment.set') && 
         hasPermission('payments.list') && 
         latestNotEndedRent && 
         (latestNotEndedRent.room || latestNotEndedRent.room_id) && (
          <button
            className="btn btn-sm btn-warning btn-action"
            onClick={() => {
              const roomId = latestNotEndedRent.room?.id || latestNotEndedRent.room_id;
              onSetPayment(roomId);
            }}
            title="Set Payment"
          >
            Set payment
          </button>
        )}

        {/* Decline Doc button - Property Assigned (3) + permission check */}
        {status_id === 3 && hasPermission('property-application.documentation.decline') && (
          <button
            className="btn btn-sm btn-danger btn-action"
            onClick={() => onDeclineDoc(guardian.id)}
            title="Decline Documentation"
          >
            Decline doc
          </button>
        )}

        {/* Set As Registered button - Declined (9) + permission check */}
        {status_id === 9 && hasPermission('guardians.canRestoreDeclined') && (
          <button
            className="btn btn-sm btn-danger btn-action"
            onClick={() => onSetAsRegistered(guardian.id)}
            title="Set As Registered (Documents Required)"
            data-confirm-action="Are you sure you want to change guardian status to Documents Required?"
          >
            Set As Registered
          </button>
        )}

        {/* Dismiss/Not Interested button - Documents Required (2) only + permission check + rent logic */}
        {/* Laravel buttons.blade.php shows only for status 2 (documents_required), not for vetted (12) */}
        {/* Laravel condition: status == 2 && permission && (!empty($latestRent) ? Dismiss : Not Interested) */}
        {status_id === 2 && hasPermission('property-application.rent.dismiss') && (
          latestRent ? (
            <button
              className="btn btn-sm btn-danger btn-action"
              onClick={() => onDismiss(guardian.id, latestRent.id)}
              title="Dismiss from property"
              data-confirm-action="Are you sure you want to dismiss guardian from property?"
            >
              Dismiss
            </button>
          ) : (
            <button
              className="btn btn-sm btn-danger btn-action"
              onClick={() => onNotInterested(guardian.id)}
              title="Mark as not interested"
              data-confirm-action="Are you sure you want to mark Guardian as Not Interested?"
            >
              Not Interested
            </button>
          )
        )}

        {/* Mark as Paid button - Payment Pending (4) + permission check + rent + initial payments exist */}
        {/* Laravel uses 'payments.manage' permission, not 'property-application.mark-as-paid' */}
        {/* Note: Laravel uses latestRent but this should be active rent for Payment Pending status */}
        {hasPermission('payments.manage') && status_id === 4 && latestNotEndedRent && hasInitialPayments && (
          <button
            className="btn btn-sm btn-info btn-action"
            onClick={() => onMarkAsPaid(latestNotEndedRent.id)}
            title="Mark as Paid"
          >
            Mark as paid
          </button>
        )}

        {/* Sign Licence Agreement button - Awaiting Key Collection (5) + permission check + rent + no signed agreement */}
        {/* Note: Laravel uses latestRent but this should be active rent for Awaiting Key Collection status */}
        {status_id === 5 && hasPermission('rents.start') && latestNotEndedRent && !hasSignedAgreement && (
          <button
            className="btn btn-sm btn-info btn-action"
            onClick={() => onSignAgreement(latestNotEndedRent.id)}
            title="Sign Licence Agreement"
          >
            Sign Licence Agreement
          </button>
        )}

        {/* Start Rent button - Awaiting Key Collection (5) + permission check + rent + signed agreement */}
        {/* Note: Laravel uses latestRent but this should be active rent for Awaiting Key Collection status */}
        {status_id === 5 && hasPermission('rents.start') && latestNotEndedRent && hasSignedAgreement && (
          <button
            className="btn btn-sm btn-success btn-action"
            onClick={() => onStartRent(latestNotEndedRent.id)}
            title="Start Rent"
          >
            Start rent
          </button>
        )}

        {/* Change Rent button - Permission check + canHaveChangedPaymentsOnRent + rent exists */}
        {/* Laravel condition: rents.payments.edit permission + canHaveChangedPaymentsOnRent() + !empty($latestRent) */}
        {/* Laravel does NOT check for room existence in condition - it just uses $latestRent->room in route */}
        {hasPermission('rents.payments.edit') && canChangeRent && latestRent && (
          <button
            className="btn btn-sm btn-info btn-action"
            onClick={() => {
              // Laravel uses $latestRent->room in route (would fail if room doesn't exist, but button still shows)
              const roomId = latestRent.room?.id || latestRent.room_id;
              if (roomId) {
                onChangeRent(roomId);
              } else {
                console.warn("⚠️ Change rent button clicked but no room_id found for rent:", latestRent.id);
                // Show error message instead of navigating to invalid route
                alert("Cannot change rent: Room information is missing for this rent.");
              }
            }}
            title="Change Rent"
          >
            Change rent
          </button>
        )}

        {/* Blacklist button - Permission check + not Blacklisted (8) */}
        {hasPermission('guardians.destroy') && status_id !== 8 && (
          <button
            className="btn btn-sm btn-danger btn-action"
            onClick={() => onBlacklist(guardian.id)}
            title="Add to Blacklist"
            data-confirm-action="Are you sure you want to add guardian to the blacklist?"
          >
            Blacklist
          </button>
        )}

        {/* Unblacklist button - Permission check + Blacklisted (8) */}
        {hasPermission('guardians.destroy') && status_id === 8 && (
          <button
            className="btn btn-sm btn-warning btn-action"
            onClick={() => onUnblacklist(guardian.id)}
            title="Remove from Blacklist"
            data-confirm-action="Are you sure you want to unblock guardian?"
          >
            Unblacklist
          </button>
        )}

        {/* Remove button - Permission check only */}
        {hasPermission('guardians.destroy') && (
          <button
            className="btn btn-sm btn-danger btn-action"
            onClick={() => onDelete(guardian.id)}
            title="Remove Guardian"
            data-confirm-action="Are you sure you want to remove?"
          >
            Remove
          </button>
        )}

        {/* Decline button - Registered (1) OR Previous Guardian (7) + permission check */}
        {[1, 7].includes(status_id) && hasPermission('guardians.edit') && (
          <button
            className="btn btn-sm btn-danger btn-action"
            onClick={() => onDecline(guardian.id)}
            title="Decline Guardian"
            data-confirm-action="Are you sure you want to decline guardian?"
          >
            Decline
          </button>
        )}

        {/* Payments button - REMOVED: Not shown in Laravel */}

        {/* Move Guardian button - REMOVED from list page */}
        {/* Laravel buttons.blade.php does NOT show Move Guardian button on list page */}
        {/* Move Guardian button should only appear on guardian show page (_tabs_buttons.blade.php) */}

        {/* Archive Guardian button - REMOVED from list page */}
        {/* Laravel buttons.blade.php does NOT show Archive button on list page */}
        {/* Archive button should only appear on guardian show page (_tabs_buttons.blade.php) */}

        {/* Send Payment Details button - REMOVED from list page */}
        {/* Laravel buttons.blade.php does NOT show Send Payment Details button on list page */}
        {/* Send Payment Details button should only appear on guardian show page (_tabs_buttons.blade.php) */}

        {/* Send Payment Reminder button - REMOVED from list page */}
        {/* Laravel buttons.blade.php does NOT show Send Payment Reminder button on list page */}
        {/* Send Payment Reminder button should only appear on guardian show page (_tabs_buttons.blade.php) */}
      </div>
    );
  };

  return (
    <div className="dataTable_wrapper" style={{ position: 'relative' }}>
      {loading && guardians.length > 0 && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255, 255, 255, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10
        }}>
          <div style={{ fontSize: '16px', color: '#333' }}>Loading...</div>
        </div>
      )}
      <table className="table table-hover dt-responsive guardian-datatable" style={{ opacity: loading && guardians.length > 0 ? 0.5 : 1 }}>
        <thead>
          <tr>
            {columnVisibility.unique_guardian_number && (
              <th
                className="sortable"
                onClick={() => handleSort("unique_guardian_number")}
                style={{ cursor: "pointer" }}
              >
                UGD<SortIcon direction={externalSortField === "unique_guardian_number" ? externalSortDirection : null} isActive={externalSortField === "unique_guardian_number"} />
              </th>
            )}
            {columnVisibility.name && (
              <th
                className="sortable"
                onClick={() => handleSort("name")}
                style={{ cursor: "pointer" }}
              >
                Name<SortIcon direction={externalSortField === "name" ? externalSortDirection : null} isActive={externalSortField === "name"} />
              </th>
            )}
            {columnVisibility.status && (
              <th
                className="sortable"
                onClick={() => handleSort("status")}
                style={{ cursor: "pointer" }}
              >
                Status<SortIcon direction={externalSortField === "status" ? externalSortDirection : null} isActive={externalSortField === "status"} />
              </th>
            )}
            {columnVisibility.email && (
              <th
                className="sortable"
                onClick={() => handleSort("email")}
                style={{ cursor: "pointer" }}
              >
                Email<SortIcon direction={externalSortField === "email" ? externalSortDirection : null} isActive={externalSortField === "email"} />
              </th>
            )}
            {columnVisibility.notes && (
              <th
                className="sortable"
                onClick={() => handleSort("notes")}
                style={{ cursor: "pointer" }}
              >
                Internal Notes<SortIcon direction={externalSortField === "notes" ? externalSortDirection : null} isActive={externalSortField === "notes"} />
              </th>
            )}
            {columnVisibility.payment_confirmed && (
              <th
                className="sortable"
                onClick={() => handleSort("payment_confirmed")}
                style={{ cursor: "pointer" }}
              >
                Payment Confirmed<SortIcon direction={externalSortField === "payment_confirmed" ? externalSortDirection : null} isActive={externalSortField === "payment_confirmed"} />
              </th>
            )}
            {columnVisibility.property_name && <th>Property</th>}
            {columnVisibility.license_fee && <th>License Fee</th>}
            {columnVisibility.placed_date && (
              <th
                className="sortable"
                onClick={() => handleSort("placed_date")}
                style={{ cursor: "pointer" }}
              >
                Placed Date<SortIcon direction={externalSortField === "placed_date" ? externalSortDirection : null} isActive={externalSortField === "placed_date"} />
              </th>
            )}
            {columnVisibility.salutation && <th>Salutation</th>}
            {columnVisibility.job_title && <th>Job Title</th>}
            {columnVisibility.address_1 && <th>Address 1</th>}
            {columnVisibility.address_2 && <th>Address 2</th>}
            {columnVisibility.address_3 && <th>Address 3</th>}
            {columnVisibility.town && <th>Town</th>}
            {columnVisibility.postcode && <th>Postcode</th>}
            {columnVisibility.hear_about && <th>Hear About</th>}
            {columnVisibility.registration_source && <th>Registration Source</th>}
            {columnVisibility.phone_number && <th>Phone Number</th>}
            {columnVisibility.mobile_number && <th>Mobile Number</th>}
            {columnVisibility.nationality && <th>Nationality</th>}
            {columnVisibility.religion && <th>Religion</th>}
            {columnVisibility.gender && <th>Gender</th>}
            {columnVisibility.date_of_birth && <th>Date of Birth</th>}
            {columnVisibility.national_insurance_number && <th>National Insurance Number</th>}
            {columnVisibility.on_newsletter_list && <th>On Newsletter List</th>}
            {columnVisibility.on_voluntary_work && <th>On Voluntary Work</th>}
            {columnVisibility.assigned_on && <th>Assigned On</th>}
            {columnVisibility.officer && <th>Officer</th>}
            {columnVisibility.email_receive_marketing && <th>Email Receive Marketing</th>}
            {columnVisibility.updated_by && <th>Updated By</th>}
            {columnVisibility.share_data_other_organisation && <th>Share Data Other Organisation</th>}
            {columnVisibility.end_date && (
              <th
                className="sortable"
                onClick={() => handleSort("end_date")}
                style={{ cursor: "pointer" }}
              >
                End Date<SortIcon direction={externalSortField === "end_date" ? externalSortDirection : null} isActive={externalSortField === "end_date"} />
              </th>
            )}
            {columnVisibility.notice_date_ntq && <th>Notice Date - NTQ</th>}
            {columnVisibility.created_at && (
              <th
                className="sortable"
                onClick={() => handleSort("created_at")}
                style={{ cursor: "pointer" }}
              >
                Created At<SortIcon direction={externalSortField === "created_at" ? externalSortDirection : null} isActive={externalSortField === "created_at"} />
              </th>
            )}
            {columnVisibility.updated_at && <th>Updated At</th>}
            {columnVisibility.actions && (
              <th className="action-head">Actions</th>
            )}
          </tr>
        </thead>
        <tbody>
          {loading && guardians.length === 0 ? (
            <tr>
              <td colSpan={Object.values(columnVisibility).filter(Boolean).length} style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ color: '#666', fontSize: '14px' }}>Loading guardians...</div>
              </td>
            </tr>
          ) : guardians.length > 0 ? (
            guardians.map((guardian) => (
            <tr key={guardian.id}>
              {columnVisibility.unique_guardian_number && (
                <td>
                  <a
                    href={`/guardians/${guardian.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#c0392b', textDecoration: 'none' }}
                  >
                    {guardian.unique_guardian_number}
                  </a>
                </td>
              )}
              {columnVisibility.name && (
                <td>{guardian.name}</td>
              )}
              {columnVisibility.status && (
                <td>{getStatusBadge(guardian.status)}</td>
              )}
              {columnVisibility.email && (
                <td>{guardian.email}</td>
              )}
              {columnVisibility.notes && (
                <td>
                  <textarea
                    name="notes"
                    className="form-control"
                    value={getNotesValue(guardian)}
                    onChange={(e) => handleNotesChange(guardian.id, e.target.value)}
                    onBlur={() => handleNotesBlur(guardian.id)}
                    style={{ minHeight: 80, width: '20rem' }}
                  />
                  {guardian.edit_date && (
                    <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                      <em>Last edited at {new Date(guardian.edit_date).toLocaleString()}</em>
                    </span>
                  )}
                </td>
              )}
              {columnVisibility.payment_confirmed && (
                <td>
                  {guardian.payment_confirmed ? (
                    <span className="badge badge-success">Yes</span>
                  ) : (
                    <span className="badge badge-warning">No</span>
                  )}
                </td>
              )}
              {columnVisibility.property_name && (
                <td>
                  {(guardian.property_name || guardian.latest_rent?.property?.name) ? (
                    (guardian.property_id || guardian.latest_rent?.property?.id) ? (
                      <a
                        href={`/properties/${guardian.property_id || guardian.latest_rent?.property?.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#c0392b', textDecoration: 'none' }}
                      >
                        {guardian.property_name || guardian.latest_rent?.property?.name}
                      </a>
                    ) : (
                      guardian.property_name || guardian.latest_rent?.property?.name
                    )
                  ) : '-'}
                </td>
              )}
              {columnVisibility.license_fee && <td>{(guardian.license_fee ?? guardian.latest_rent?.license_fee) != null ? `£${Number(guardian.license_fee ?? guardian.latest_rent?.license_fee).toFixed(2)}` : '-'}</td>}
              {columnVisibility.placed_date && <td>{guardian.placed_date || guardian.placedDate ? new Date(guardian.placed_date || guardian.placedDate).toLocaleDateString('en-GB') : '-'}</td>}
              {columnVisibility.salutation && <td>{guardian.user?.salutation || '-'}</td>}
              {columnVisibility.job_title && <td>{guardian.user?.job_title || '-'}</td>}
              {columnVisibility.address_1 && <td>{guardian.user?.address_1 || '-'}</td>}
              {columnVisibility.address_2 && <td>{guardian.user?.address_2 || '-'}</td>}
              {columnVisibility.address_3 && <td>{guardian.user?.address_3 || '-'}</td>}
              {columnVisibility.town && <td>{guardian.user?.town || '-'}</td>}
              {columnVisibility.postcode && <td>{guardian.user?.postcode || '-'}</td>}
              {columnVisibility.hear_about && <td>{guardian.hearAbout?.name || '-'}</td>}
              {columnVisibility.registration_source && <td>{guardian.registration_source_label || 'Direct'}</td>}
              {columnVisibility.phone_number && <td>{guardian.user?.phone_number || '-'}</td>}
              {columnVisibility.mobile_number && <td>{guardian.user?.mobile_number || '-'}</td>}
              {columnVisibility.nationality && <td>{guardian.country?.nationality || '-'}</td>}
              {columnVisibility.religion && <td>{guardian.religion?.name || '-'}</td>}
              {columnVisibility.gender && <td>{guardian.gender || '-'}</td>}
              {columnVisibility.date_of_birth && <td>{guardian.date_of_birth ? new Date(guardian.date_of_birth).toLocaleDateString('en-GB') : '-'}</td>}
              {columnVisibility.national_insurance_number && <td>{guardian.national_insurance_number || '-'}</td>}
              {columnVisibility.on_newsletter_list && <td>{guardian.on_newsletter_list ? 'Yes' : 'No'}</td>}
              {columnVisibility.on_voluntary_work && <td>{guardian.on_voluntary_work ? 'Yes' : 'No'}</td>}
              {columnVisibility.assigned_on && <td>{guardian.assigned_on ? new Date(guardian.assigned_on).toLocaleDateString('en-GB') : '-'}</td>}
              {columnVisibility.officer && <td>{guardian.officer || '-'}</td>}
              {columnVisibility.email_receive_marketing && <td>{guardian.emails_receive_marketing ? 'Yes' : 'No'}</td>}
              {columnVisibility.updated_by && <td>{guardian.editor || '-'}</td>}
              {columnVisibility.share_data_other_organisation && <td>{guardian.emails_share_data_other_organisations ? 'Yes' : 'No'}</td>}
              {columnVisibility.end_date && <td>{guardian.rent_end_date || guardian.rentEndDate ? new Date(guardian.rent_end_date || guardian.rentEndDate).toLocaleDateString('en-GB') : '-'}</td>}
              {columnVisibility.notice_date_ntq && <td>{guardian.notice_date || guardian.noticeDate ? new Date(guardian.notice_date || guardian.noticeDate).toLocaleDateString('en-GB') : '-'}</td>}
              {columnVisibility.created_at && <td>{(guardian.created_at || guardian.createdAt) ? new Date(guardian.created_at || guardian.createdAt).toLocaleDateString('en-GB') : '-'}</td>}
              {columnVisibility.updated_at && <td>{(guardian.updated_at || guardian.updatedAt) ? new Date(guardian.updated_at || guardian.updatedAt).toLocaleDateString('en-GB') : '-'}</td>}
              {columnVisibility.actions && (
                <td>{renderActionButtons(guardian)}</td>
              )}
            </tr>
            ))
          ) : (
            <tr>
              <td colSpan={Object.values(columnVisibility).filter(Boolean).length} style={{ textAlign: 'center', padding: '20px' }}>
                <div style={{ color: '#666', fontSize: '14px' }}>
                  {guardians.length === 0 ? 'No guardians found.' : 'No guardians match the current filters.'}
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Server-side Pagination Controls */}
      {totalRecords > 0 && (
        <div className="pagination-container" style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Pagination Info */}
          <div style={{ fontSize: '14px', color: '#666' }}>
            Showing {startIndex + 1} to {endIndex} of {totalRecords} guardians
          </div>

          {/* Pagination Controls */}
          <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
            {/* Page Size Selector */}
            <div style={{ marginRight: '20px' }}>
              <label style={{ marginRight: '5px', fontSize: '14px' }}>Show:</label>
              <select
                value={pageSize}
                onChange={(e) => {
                  onPageSizeChange(Number(e.target.value));
                  onPageChange(1);
                }}
                style={{ padding: '5px' }}
              >
                {[10, 25, 50, 100].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <span style={{ marginLeft: '5px', fontSize: '14px' }}>entries</span>
            </div>

            {/* Page Navigation */}
            <button
              className="btn btn-sm"
              onClick={() => onPageChange(1)}
              disabled={currentPage === 1}
              style={{ padding: '5px 10px' }}
            >
              First
            </button>
            <button
              className="btn btn-sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              style={{ padding: '5px 10px' }}
            >
              Previous
            </button>

            {/* Page Numbers */}
            {(() => {
              const maxPagesToShow = 5;
              let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
              let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

              if (endPage - startPage + 1 < maxPagesToShow) {
                startPage = Math.max(1, endPage - maxPagesToShow + 1);
              }

              const pageNumbers = [];
              for (let i = startPage; i <= endPage; i++) {
                pageNumbers.push(i);
              }

              return (
                <>
                  {startPage > 1 && (
                    <>
                      <button className="btn btn-sm" onClick={() => onPageChange(1)}>1</button>
                      {startPage > 2 && <span style={{ padding: '5px' }}>...</span>}
                    </>
                  )}

                  {pageNumbers.map((page) => (
                    <button
                      key={page}
                      className={`btn btn-sm ${currentPage === page ? 'btn-primary' : 'btn-default'}`}
                      onClick={() => onPageChange(page)}
                      style={{
                        padding: '5px 10px',
                        backgroundColor: currentPage === page ? '#007bff' : 'transparent',
                        color: currentPage === page ? '#fff' : '#333',
                        fontWeight: currentPage === page ? 'bold' : 'normal'
                      }}
                    >
                      {page}
                    </button>
                  ))}

                  {endPage < totalPages && (
                    <>
                      {endPage < totalPages - 1 && <span style={{ padding: '5px' }}>...</span>}
                      <button className="btn btn-sm" onClick={() => onPageChange(totalPages)}>{totalPages}</button>
                    </>
                  )}
                </>
              );
            })()}

            <button
              className="btn btn-sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              style={{ padding: '5px 10px' }}
            >
              Next
            </button>
            <button
              className="btn btn-sm"
              onClick={() => onPageChange(totalPages)}
              disabled={currentPage === totalPages}
              style={{ padding: '5px 10px' }}
            >
              Last
            </button>

            <span style={{ marginLeft: '15px', fontSize: '14px', color: '#666' }}>
              Page {currentPage} of {totalPages}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuardianDataTable; 